/**
 * Descriptor de Pessoa — a entidade-piloto. Cobre todos os campos de `PessoaOut`
 * (nada oculto): identificação, documentos, origem/filiação e o bloco de sistema.
 * Correlações: telefones, documentos anexos e o dossiê (ancorado no CPF).
 */
import { IdCard } from 'lucide-react';
import { createElement } from 'react';
import { PainelAchados } from '@/components/crud/paineis/PainelAchados';
import { PainelFiliacao } from '@/components/crud/paineis/PainelFiliacao';
import { PainelFontesPublicas } from '@/components/crud/paineis/PainelFontesPublicas';
import { PainelParentela } from '@/components/crud/paineis/PainelParentela';
import type { PessoaCad } from '@/lib/api';
import { idadeDeData } from '@/lib/datas';
import { capitalizarNome } from '@/lib/nomes';
import { definirRecurso } from '@/lib/recursos/tipos';

const SEXO = [
  { valor: 'M', rotulo: 'Masculino' },
  { valor: 'F', rotulo: 'Feminino' },
  { valor: 'outro', rotulo: 'Outro' },
];

const ESTADO_CIVIL = [
  { valor: 'solteiro', rotulo: 'Solteiro(a)' },
  { valor: 'casado', rotulo: 'Casado(a)' },
  { valor: 'uniao_estavel', rotulo: 'União estável' },
  { valor: 'separado', rotulo: 'Separado(a)' },
  { valor: 'divorciado', rotulo: 'Divorciado(a)' },
  { valor: 'viuvo', rotulo: 'Viúvo(a)' },
];

const ESCOLARIDADE = [
  { valor: 'analfabeto', rotulo: 'Analfabeto' },
  { valor: 'fundamental_incompleto', rotulo: 'Fundamental incompleto' },
  { valor: 'fundamental_completo', rotulo: 'Fundamental completo' },
  { valor: 'medio_incompleto', rotulo: 'Médio incompleto' },
  { valor: 'medio_completo', rotulo: 'Médio completo' },
  { valor: 'superior_incompleto', rotulo: 'Superior incompleto' },
  { valor: 'superior_completo', rotulo: 'Superior completo' },
  { valor: 'pos_graduacao', rotulo: 'Pós-graduação' },
  { valor: 'mestrado', rotulo: 'Mestrado' },
  { valor: 'doutorado', rotulo: 'Doutorado' },
];

export const pessoa = definirRecurso<PessoaCad>({
  chave: 'pessoas',
  endpoint: '/cadastro/pessoas',
  ownerTipo: 'pessoa',
  titulo: 'Pessoas',
  tituloSingular: 'pessoa',
  descricao: 'Pessoas físicas — identificação, documentos, contatos e correlação.',
  icone: IdCard,
  capacidades: { podeEditar: true, podeExcluir: true },
  camposFiltro: [
    // Derivada por trigger a partir da data de nascimento — não se digita, mas é o filtro mais
    // pedido numa base previdenciária ("quem está entre 60 e 75").
    { chave: 'idade', rotulo: 'Idade', tipo: 'numero' },
  ],
  buscaPlaceholder: 'Buscar por nome ou CPF',
  rotuloItem: (p) => p.nome,

  colunas: [
    { chave: 'nome', rotulo: 'Nome' },
    { chave: 'cpf', rotulo: 'CPF', formato: 'cpf', mono: true },
    { chave: 'data_nascimento', rotulo: 'Nascimento', formato: 'data', ocultarEm: 'sm', mono: true },
    { chave: 'idade', rotulo: 'Idade', ocultarEm: 'md', alinhamento: 'direita', mono: true },
  ],

  // Só o nome é exigido. CPF, e-mail e telefone são os "dados principais" opcionais — e-mail e
  // telefone já nascem vinculados como contato (dedup + correlação); o resto, na tela de edição.
  camposCriar: [
    { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true, mutator: capitalizarNome },
    { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
    { chave: 'email', rotulo: 'E-mail', tipo: 'texto' },
    { chave: 'telefone', rotulo: 'Telefone', tipo: 'telefone' },
  ],

  // Cadastro por documento: anexa RG/CNH/CPF/Título…, a IA lê os dados e o operador revisa antes de
  // gravar — ou insere à mão. O tipo é escolhido no drawer (pessoa tem vários docs de identidade).
  importar: {
    rotulo: 'Importar documento',
    selecionavel: true,
    corpoMinimo: { nome: 'Cadastro por documento' },
    descricao:
      'Anexe um documento de identidade (RG, CNH, CPF, Título…). A IA lê nome, CPF e demais dados; você revisa antes de gravar.',
  },

  // Ingestão em massa: sobe um Excel/CSV com muitas pessoas; a IA sugere o mapa coluna→campo
  // (inclui profissão, contatos, endereço e emprego), o operador revisa, e a importação roda
  // assíncrona com progresso. O layout revisado vira um molde reutilizável.
  importarPlanilha: {
    endpoint: '/cadastro/ingestao/pessoas',
    descricao:
      'Suba uma planilha (Excel ou CSV) com muitas pessoas. A IA sugere para que serve cada coluna — inclusive profissão, contatos, endereço e emprego; você revisa antes de importar.',
  },

  gruposEdicao: [
    {
      id: 'identificacao',
      titulo: 'Identificação',
      campos: [
        { chave: 'nome', rotulo: 'Nome', tipo: 'texto', obrigatorio: true, colSpan: 2, mutator: capitalizarNome },
        { chave: 'nome_social', rotulo: 'Nome social', tipo: 'texto', colSpan: 2, mutator: capitalizarNome },
        { chave: 'cpf', rotulo: 'CPF', tipo: 'documento', formato: 'cpf' },
        // A idade é derivada da data de nascimento (trigger no banco + cron) — não é campo próprio;
        // aparece colada à data, calculada na tela conforme o operador digita.
        {
          chave: 'data_nascimento',
          rotulo: 'Data de nascimento',
          tipo: 'data',
          sufixo: (v) => {
            const anos = idadeDeData(v);
            return anos == null ? null : `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
          },
        },
        { chave: 'sexo', rotulo: 'Sexo', tipo: 'select', opcoes: SEXO },
        { chave: 'estado_civil', rotulo: 'Estado civil', tipo: 'select', opcoes: ESTADO_CIVIL },
      ],
    },
    {
      id: 'documentos',
      titulo: 'Documentos',
      campos: [
        { chave: 'rg', rotulo: 'RG', tipo: 'documento', formato: 'rg' },
        { chave: 'rg_orgao_emissor', rotulo: 'Órgão emissor', tipo: 'texto' },
        { chave: 'rg_uf', rotulo: 'UF do RG', tipo: 'texto' },
        { chave: 'rg_expedicao', rotulo: 'Expedição do RG', tipo: 'data' },
        { chave: 'cnh', rotulo: 'CNH', tipo: 'texto' },
        { chave: 'cnh_categoria', rotulo: 'Categoria CNH', tipo: 'texto' },
        { chave: 'cnh_validade', rotulo: 'Validade CNH', tipo: 'data' },
        { chave: 'titulo_eleitor', rotulo: 'Título de eleitor', tipo: 'texto' },
        { chave: 'pis_nis', rotulo: 'PIS/NIS', tipo: 'documento', formato: 'pis' },
        { chave: 'passaporte', rotulo: 'Passaporte', tipo: 'texto' },
      ],
    },
    {
      id: 'origem',
      titulo: 'Origem e filiação',
      campos: [
        { chave: 'nacionalidade', rotulo: 'Nacionalidade', tipo: 'texto' },
        { chave: 'naturalidade_cidade', rotulo: 'Naturalidade (cidade)', tipo: 'texto' },
        { chave: 'naturalidade_uf', rotulo: 'Naturalidade (UF)', tipo: 'texto' },
        { chave: 'nome_pai', rotulo: 'Nome do pai', tipo: 'texto', colSpan: 2, mutator: capitalizarNome },
        { chave: 'nome_mae', rotulo: 'Nome da mãe', tipo: 'texto', colSpan: 2, mutator: capitalizarNome },
        { chave: 'escolaridade', rotulo: 'Escolaridade', tipo: 'select', opcoes: ESCOLARIDADE },
        {
          chave: 'profissao_id',
          rotulo: 'Profissão',
          tipo: 'referencia',
          refEndpoint: '/cadastro/profissoes',
        },
        { chave: 'renda_estimada', rotulo: 'Renda estimada (mensal)', tipo: 'dinheiro' },
      ],
    },
    {
      id: 'sistema',
      titulo: 'Situação e observações',
      campos: [
        { chave: 'ativo', rotulo: 'Ativo', tipo: 'booleano' },
        { chave: 'observacoes', rotulo: 'Observações', tipo: 'texto-longo', colSpan: 2 },
        { chave: 'created_at', rotulo: 'Criado em', tipo: 'leitura' },
        { chave: 'updated_at', rotulo: 'Atualizado em', tipo: 'leitura' },
      ],
    },
  ],

  paineis: [
    { tipo: 'contatos' },
    { tipo: 'enderecos' },
    { tipo: 'relacoes' },
    { tipo: 'empregos' },
    { tipo: 'documentos' },
    { tipo: 'dossie' },
  ],

  // A edição em abas (o pedido): dados, contatos, endereços, e as duas famílias de relação.
  // O Dossiê fecha como a visão de correlação — o mapa de quem se liga a quem.
  abas: [
    {
      id: 'dados',
      rotulo: 'Dados Pessoais',
      grupos: ['identificacao', 'documentos', 'origem', 'sistema'],
      paineis: [{ tipo: 'tags' }],
    },
    { id: 'contatos', rotulo: 'Contatos', paineis: [{ tipo: 'contatos' }] },
    { id: 'enderecos', rotulo: 'Endereços', paineis: [{ tipo: 'enderecos' }] },
    {
      id: 'relacoes',
      rotulo: 'Relações Pessoais',
      paineis: [
        { tipo: 'relacoes' },
        // A filiação que a ficha declara por NOME, procurada no cadastro. Fica logo abaixo das
        // relações porque é a mesma matéria: ali estão os parentescos que existem, aqui os que a
        // ficha afirma e o grafo ainda não tem.
        {
          tipo: 'custom',
          id: 'pessoa-filiacao',
          titulo: 'Filiação declarada',
          render: ({ item, recarregar }) =>
            createElement(PainelFiliacao, { item, recarregar }),
        },
        // A travessia: quem são os parentes a 1, 2 ou 3 passos, com o caminho à vista.
        {
          tipo: 'custom',
          id: 'pessoa-parentela',
          titulo: 'Parentela',
          render: ({ item }) => createElement(PainelParentela, { item }),
        },
        // O parentesco DERIVADO, logo depois do declarado. A ordem da aba conta a história: o que
        // está registrado, o que a ficha afirma e ainda não é aresta, e o que o resto da base
        // sugere. Nada do terceiro grupo foi gravado — é leitura, e quem confirma é o operador.
        {
          tipo: 'custom',
          id: 'pessoa-parentesco-descoberto',
          titulo: 'Parentesco que ninguém digitou',
          render: ({ item }) =>
            createElement(PainelAchados, {
              dominio: 'parentesco',
              alvo: { tipo: 'pessoa', id: item.id },
            }),
        },
      ],
    },
    { id: 'comerciais', rotulo: 'Relações Comerciais', paineis: [{ tipo: 'empregos' }] },
    // Aba dedicada: anexar documentos e EXTRAIR seus dados por IA (revisão antes de gravar).
    { id: 'documentos', rotulo: 'Documentos', paineis: [{ tipo: 'documentos' }] },
    // Fonte pública fica ANTES do dossiê de propósito: o dossiê correlaciona o que a base já tem,
    // e esta aba é por onde entra o que a base ainda NÃO tem. Nada aqui grava na ficha.
    {
      id: 'fontes',
      rotulo: 'Fontes Públicas',
      paineis: [
        {
          tipo: 'custom',
          id: 'pessoa-fontes-publicas',
          titulo: 'Fontes públicas',
          render: ({ item }) => createElement(PainelFontesPublicas, { item, tipo: 'pessoa' }),
        },
      ],
    },
    { id: 'dossie', rotulo: 'Dossiê', paineis: [{ tipo: 'dossie' }] },
    { id: 'grafo', rotulo: 'Grafo', paineis: [{ tipo: 'grafo' }] },
  ],
});
